import React, { useCallback, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import classNames from 'classnames';
import { AppBar } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import ProximityAd from 'components/Ad/ProximityAd';
import AdhesionAd from 'components/Ad/AdhesionAd';
import LinkBank from 'components/SeoSrp/LinkBank';
import News from 'components/SeoSrp/News';
import TopDealers from 'components/SeoSrp/TopDealers';

import useSrpFilterStateInitialization from 'lib/hooks/useSrpFilterStateInitialization';
import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import useFetchLeads from 'lib/hooks/useFetchLeads';
import useAdSetup from 'lib/hooks/useAdSetup';

import { MEDIUM_RECTANGLE, HALF_PAGE_SKYSCRAPER } from 'lib/GoogleAds/Sizes';
import { Zone } from 'lib/CarGurusAds/TaxonomySchema';
import CarGurusAdUnit from 'lib/CarGurusAds/AdUnit';
import {
  VehicleOverviewInterface,
  VehicleV2,
  ArticlePreview,
  DrivingExperiences,
  TopDealers as TopDealersType,
  SearchV2AdInfo,
  fetchVehicle,
} from 'lib/AutolistAPI';

import {
  DealerReviewsInterface,
  SearchUrlAttributes,
  ModelReviewsInterface,
} from 'types/Apis/SearchURL';
import State from 'types/State';
import { VehicleInfo } from 'types/Apis/VehicleApiResponse';
import DealerReviews from 'components/SeoSrp/DealerReviews';
import OwnerReviews from 'components/SeoSrp/OwnerReviews';
import VehichleOverview from 'components/SeoSrp/VehicleOverview';
import DrivingTipsAndExperience from 'components/SeoSrp/DrivingTips';
import { useContentGroups } from 'lib/hooks/useGoogleAnalytics';
import { setSrpScrollPosition } from 'redux-implementations/vdpScroll';
import EmptyReviewsCTA from 'components/SeoSrp/EmptyReviewsCTA/EmptyReviewsCTA';
import i18n from 'lib/i18next';
import useEvents from 'lib/hooks/useEvents';
import searchResultEvent from 'lib/Tracking/Srp/SearchResultView';
import { SourcePages } from 'lib/EventSchema';
import OnboardingBanner from 'components/Onboarding/OnboardingBanner';
import OnboardingModal from 'components/Onboarding/Modal';
import tapEvent from 'lib/Tracking/Onboarding/TapEvent';
import useHashLocation from 'lib/hooks/useHashLocation';
import BackToTopButton from './BackToTopButton';
import Sidebar from './Sidebar';
import ResultsList from '../Shared/ResultsList';
import DesktopSearchBar from './SearchBar/Desktop';
import MobileSearchBar from './SearchBar/Mobile';
import FilterPills from './FilterPills/FilterPills';
import { styles, useStyles } from './styles';
import ResultCount from './ResultCount';
import PhotoDisclaimer from './PhotoDisclaimer';

export interface SrpProps {
  adInfo?: SearchV2AdInfo;
  vehicles: VehicleV2[];
  totalCount: number;
  isLoading: boolean;
  searchVehicles: () => Promise<void>;
  searchUrlProps?: SearchUrlAttributes;
  dealerReviewsProps?: DealerReviewsInterface;
  ownerReviewsProps?: ModelReviewsInterface;
  vehicleOverview?: VehicleOverviewInterface;
  articlePreviews?: ArticlePreview[];
  make?: string;
  model?: string;
  drivingTipsAndExperiences?: DrivingExperiences;
  contentGroup?: string;
  topDealers?: TopDealersType;
}

const topDealersPages = ['LocationSearchUrl'];

const ownerReviewPages = [
  'MakeModelYearSearchUrl',
  'MakeModelLocationSearchUrl',
  'MakeModelSearchUrl',
  'BodyStyleSearchUrl',
  'MakeSearchUrl',
];

const dealerReviewPages = [
  'LocationSearchUrl',
  'BodyStyleLocationSearchUrl',
  'CategoricalLocationSearchUrl',
  'MakeModelLocationSearchUrl',
];

const excludeDrivingTipsAndExperiencePages = [
  'BodyStyleLocationSearchUrl',
  'CategoricalLocationSearchUrl',
  'MakeModelLocationSearchUrl',
];

function OwnerReviewsSection(
  ownerReviewsProps?: ModelReviewsInterface,
  searchUrlProps?: SearchUrlAttributes
): JSX.Element {
  const showOwnerReviews = !!ownerReviewsProps && ownerReviewsProps.ownerReviews.length > 0;
  const ownerReviewsLink =
    'https://docs.google.com/forms/d/e/1FAIpQLSeUGpTgb_54f6TLvU8Vj74mlZxd8pBzhGrpsYBcslCHcYzcrw/viewform';

  const constructHeader = (): string => {
    if (searchUrlProps?.search_params.body_style) {
      return `${searchUrlProps.search_params.body_style} ${i18n.t('seoSrp.ownerReviews.header')}`;
    }

    if (searchUrlProps?.year) {
      return `${searchUrlProps.year} ${searchUrlProps.make_name} ${
        searchUrlProps.model_name
      } ${i18n.t('seoSrp.ownerReviews.header')}`;
    }

    if (searchUrlProps?.location_display_name) {
      return `${searchUrlProps.make_name} ${searchUrlProps.model_name} ${
        searchUrlProps.location_display_name
      } ${i18n.t('seoSrp.ownerReviews.header')}`;
    }

    if (searchUrlProps?.model_name) {
      return `${searchUrlProps?.make_name} ${searchUrlProps?.model_name} ${i18n.t(
        'seoSrp.ownerReviews.header'
      )}`;
    }

    return `${searchUrlProps?.make_name} ${i18n.t('seoSrp.ownerReviews.header')}`;
  };

  return (
    <>
      {showOwnerReviews ? (
        <OwnerReviews
          reviews={ownerReviewsProps}
          pageType={searchUrlProps?.type || ''}
          header={constructHeader()}
        />
      ) : (
        <EmptyReviewsCTA
          link={ownerReviewsLink}
          header={i18n.t('seoSrp.ownerReviews.aggregateCard.header')}
        />
      )}
    </>
  );
}

function DealerReviewsSection(
  locationDisplayName?: string,
  dealerReviewsProps?: DealerReviewsInterface
): JSX.Element {
  const showDealerReviews =
    !!locationDisplayName && dealerReviewsProps && dealerReviewsProps.dealerReviews.length > 0;
  const dealerReviewsLink =
    'https://docs.google.com/forms/d/e/1FAIpQLSe-OmCY-WFRnwRt8YsbSzSob1IOmnwCU9TlhxAm4Vdx9O8e1g/viewform';

  return (
    <>
      {showDealerReviews ? (
        <DealerReviews locationDisplayName={locationDisplayName} reviews={dealerReviewsProps} />
      ) : (
        <EmptyReviewsCTA locationDisplayName={locationDisplayName} link={dealerReviewsLink} />
      )}
    </>
  );
}

export default function NewSrp({
  adInfo,
  vehicles,
  isLoading,
  totalCount,
  searchVehicles,
  searchUrlProps,
  dealerReviewsProps,
  ownerReviewsProps,
  vehicleOverview,
  articlePreviews,
  make,
  model,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  drivingTipsAndExperiences,
  contentGroup,
  topDealers,
}: SrpProps): JSX.Element {
  useContentGroups({ default: contentGroup });
  const { isMobile } = useDeviceScreen();
  const [searchFiltersViewRef, searchFiltersInView] = useInView();
  const [vehicleDetails, setVehicleDetails] = useState<VehicleInfo[]>([]);
  const dispatch = useDispatch();
  const srpScrollPosition = useSelector<State>(
    (state) => state.vdpScroll.srpScrollPosition
  ) as number;
  useSrpFilterStateInitialization();
  useFetchLeads();
  const { page } = useHashLocation().values;

  /* Check whether this ad info will change in SEO SRP */
  const classes = useStyles();

  /* Determine how adUnitConfig is retrieved for SRP vs SEO SRP */
  const adUnitConfig = { zone: Zone.SRP };
  const adUnit = new CarGurusAdUnit(adUnitConfig);
  const adUnitString = (adInfo?.adUnit as string) || adUnit.toString();

  const hasTopDealers = !!contentGroup && topDealersPages.includes(contentGroup);
  const hasOwnerReviewsSection = !!contentGroup && ownerReviewPages.includes(contentGroup);

  const { location_display_name: locationDisplayName } = searchUrlProps || {};
  const hasDealerReviewsSection = !!contentGroup && dealerReviewPages.includes(contentGroup);

  const hasDrivingTipsAndExperienceSection =
    !!contentGroup && !excludeDrivingTipsAndExperiencePages.includes(contentGroup);

  useAdSetup(adUnitString, {
    adUnit: adUnitString,
    make: adInfo?.make,
    model: adInfo?.model,
    year: adInfo?.year,
    pageType: adInfo?.pageType,
    ageType: adInfo?.ageType,
    cpo: adInfo?.cpo,
    inLine: adInfo?.inLine,
    body_style: adInfo?.body_style,
    category: adInfo?.category,
    app: adInfo?.app,
  });

  const logger = useEvents();
  const logSearchResultEvent = useCallback(() => {
    vehicles.forEach((vehicle, index) => {
      logger.log(
        searchResultEvent({
          trackingParams: vehicle.trackingParams,
          sourcePage: SourcePages.Srp,
          page,
          searchResultIndex: index.toString(),
          vin: vehicle.vin,
        })
      );
    });
  }, [vehicles, logger, page]);

  useEffect(() => {
    const fetchVehicleDetails = async (): Promise<void> => {
      const details = await Promise.all(vehicles.map(async (vehicle) => fetchVehicle(vehicle.vin)));
      setVehicleDetails(details);
    };
    fetchVehicleDetails();
  }, [vehicles]);

  useEffect(() => {
    logSearchResultEvent();
  }, [logSearchResultEvent]);

  useEffect(() => {
    if (srpScrollPosition) {
      setTimeout(() => window.scrollTo({ top: srpScrollPosition }), 100);
      dispatch(setSrpScrollPosition(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <div className="srp-outer">
        <BackToTopButton />

        <AppBar position="sticky" className={classes.appBar} elevation={0}>
          <MobileSearchBar totalCount={totalCount} searchVehicles={searchVehicles} isSeo={true} />
        </AppBar>
        <div className="srp-inner">
          <div className="srp-sidebar">
            <div ref={searchFiltersViewRef}>
              <Sidebar />
            </div>
            <ProximityAd
              adConfig={{
                targeting: { inLine: 2 },
                dimensions: [[300, 250]],
              }}
              placeholderSize={MEDIUM_RECTANGLE}
              className={`sidebar-ad`}
            />
            <div
              className={classNames('sidebar-ads', {
                'sticky-sidebar-ads': !searchFiltersInView,
              })}
            >
              <ProximityAd
                adConfig={{
                  targeting: { inLine: 3 },
                  dimensions: [[300, 250]],
                }}
                placeholderSize={MEDIUM_RECTANGLE}
                className={`sidebar-ad`}
              />
              <ProximityAd
                adConfig={{
                  targeting: { inLine: 4 },
                  dimensions: [[300, 600]],
                }}
                placeholderSize={HALF_PAGE_SKYSCRAPER}
                className={`sidebar-ad`}
              />
            </div>
          </div>
          <div className="srp-right-container">
            <DesktopSearchBar totalCount={totalCount} searchVehicles={searchVehicles} />
            <div className="filters-desktop">
              <ResultCount
                totalCount={totalCount}
                make={make}
                model={model}
                isLoading={isLoading}
              />
              <FilterPills />
            </div>
            {
              <OnboardingBanner
                onClick={(): void => {
                  setIsModalOpen(true);
                  logger.log(
                    tapEvent({ action: 'onboarding_start', sourcePage: SourcePages.SeoSrp })
                  );
                }}
              />
            }
            <OnboardingModal
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
              isPending={isLoading}
            />
            <ResultsList
              vehicles={vehicles}
              totalCount={totalCount}
              isLoading={isLoading}
              searchVehicles={searchVehicles}
              vehicleDetails={vehicleDetails}
            />
            {searchUrlProps && (
              <>
                <div className="full-width">
                  {hasTopDealers && topDealers && (
                    <TopDealers
                      dealers={topDealers}
                      locationDisplayName={searchUrlProps.location_display_name}
                    />
                  )}
                  {hasOwnerReviewsSection && OwnerReviewsSection(ownerReviewsProps, searchUrlProps)}
                  {hasDealerReviewsSection &&
                    DealerReviewsSection(locationDisplayName, dealerReviewsProps)}
                  {hasDrivingTipsAndExperienceSection &&
                    drivingTipsAndExperiences?.experiences.length && (
                      <DrivingTipsAndExperience drivingExperiences={drivingTipsAndExperiences} />
                    )}
                  {vehicleOverview && <VehichleOverview vehicleOverview={vehicleOverview} />}
                  {!!articlePreviews?.length && (
                    <News articles={articlePreviews} make={make} model={model} />
                  )}
                  {searchUrlProps.link_set.map((set) => (
                    <React.Fragment key={set.title}>
                      <LinkBank linkSet={set} />
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {isMobile && <AdhesionAd adhereToBottom={true} targeting={11} />}
        <PhotoDisclaimer />
      </div>
      <style jsx>{styles}</style>
    </>
  );
}
